import { Thumbnail } from 'mm-ui-components/dist/src/base/partials/image/image.types';
import { Article, Image } from '../template.state.types';
import {
    INJECT_ARTICLE_BLOCK_BY_INDEX_ARRAY,
} from './postPage.actions';
import { LoadMoreAction, RECEIVED_POSTS_SUCCESSFULLY, RECEIVED_POSTS_UNSUCCESSFULLY } from '../loadMore.actions';

type Block = {
    type: string;
    [key: string]: any;
    dataId: string;
};

export type Body = Array<Block>;

export interface Cover {
    image: Image;
    media: Media | null;
}

interface Media {
    html: string;
    type: string;
    videoId?: string;
}

interface ShareConfigItem {
    shareURL: string;
    src: string;
    type: string;
}

export type ShareConfig = Array<ShareConfigItem>;

export interface Recommendation {
    articles: Array<Article>;
    next: string;
    displayName: string;
    text: string;
    link: string;
}

export type RecommendationNames = 'recommendation1' | 'recommendation2';

export interface SponsorData {
    text: string;
    image: Image;
}

export interface AuthorData {
    name: string;
    link?: string;
    image?: Thumbnail | null;
    byline?: string;
}

export type AuthorsList = AuthorData[];

export interface Slideshow {
    slideNumber: number;
    totalSlides: number;
    prevText: string;
    nextText: string;
    prevLink: string;
    nextLink: string;
}

export interface PostPageState {
    title: string;
    by: string;
    authors: AuthorsList | null;
    owner: AuthorData;
    cover: Cover;
    updatedAtDateFormatted: string;
    updatedAtTimeAndDateFormatted: string;
    createdAtDateFormatted: string;
    createdAtTimeAndDateFormatted: string;
    createdAt: string;
    updatedAt: string;
    shareConfig: ShareConfig;
    body: Body | null;
    articleId: string;
    channels: Array<string>;
    tags: Array<string>;
    noIndex: boolean;
    recommendation1: Recommendation;
    recommendation2: Recommendation;
    sponsor: null | SponsorData;
    commercialTags: Array<string>;
    dfpCategory?: string;
    numberOfWordsTyped: number;
    contentTypesToQuantity: { [key: string]: number };
    ogDescription: string;
    ogTitle: string;
    ogImage: Image;
    twitterDescription: string;
    twitterTitle: string;
    twitterImage: Image;
    metadataDescription: string;
    metadataTitle: string;
    metadataAdsCategory: string | null;
    slideshow: Slideshow;
}

const INJECTED_BLOCK_TYPE = 'injectedBlock';

interface InjectedBlock {
    index: number;
    block: {
        type: typeof INJECTED_BLOCK_TYPE;
        html: string;
        dataId: string;
        script: string | undefined;
    };
}

export interface InjectArticleBlockByIndexArrayAction {
    type: typeof INJECT_ARTICLE_BLOCK_BY_INDEX_ARRAY;
    blocks: Array<InjectedBlock>;
}

export type PostPageAction = LoadMoreAction<RecommendationNames> | InjectArticleBlockByIndexArrayAction;

const sortByIndexComparator = (block1: InjectedBlock, block2: InjectedBlock) => {
    if (block1.index < block2.index) {
        return -1;
    }
    if (block1.index > block2.index) {
        return 1;
    }
    return 0;
};

const mergeBlocks = (blocks: Array<InjectedBlock>, body: Body) => {
    let injectedBlocksCounter = 0;
    return blocks.reduce((acc, { index, block }) => {
        const injectedBlockIndex = index + injectedBlocksCounter;
        const bodyWithInjectedBlock = [...acc.slice(0, injectedBlockIndex), block, ...acc.slice(injectedBlockIndex)];
        injectedBlocksCounter += 1;
        return bodyWithInjectedBlock;
    }, body);
};

export const postPageReducer = (state: PostPageState = {} as PostPageState, action: PostPageAction) => {
    switch (action.type) {
        case RECEIVED_POSTS_SUCCESSFULLY: {
            return {
                ...state,
                [action.sectionKey]: {
                    ...state[action.sectionKey],
                    articles: action.articles,
                },
            };
        }

        case RECEIVED_POSTS_UNSUCCESSFULLY: {
            return state;
        }

        case INJECT_ARTICLE_BLOCK_BY_INDEX_ARRAY: {
            const { body } = state;
            const { blocks } = action;
            const sortedBlocks = blocks.sort(sortByIndexComparator);
            const mergeBlocksToBody = mergeBlocks(sortedBlocks, body || []);
            return {
                ...state,
                body: mergeBlocksToBody,
            };
        }

        default:
            return state;
    }
};
